import { HTMLAttributes, useMemo } from 'react';
import { css } from '@emotion/react';
import { Icon } from '@frontend/icons';
import { theme } from '@frontend/theme';
import { NakedButton, Tabs, Text, useTooltip } from '@frontend/design-system';
import { TrendsContextValue, TrendsProvider, useTrendsContext } from '../providers.tsx/index.js';

type HeaderProps = {
  duration?: string;
  isLimitedData?: boolean;
  title: string;
  tooltipContent?: string;
  trackingId?: string;
};

type ContentProps = { children: React.ReactNode } & React.ComponentProps<typeof Tabs>;

type ViewProps = {
  children: React.ReactNode;
} & React.ComponentProps<typeof Tabs.Panel>;

interface PatientCallTrendsProps<T extends string> extends HTMLAttributes<HTMLDivElement> {
  activeTab: T;
  defaultTab: T;
  isLoading: boolean;
  isOpen: boolean;
  onTabChange: (tab: T) => void;
  onToggle: (open: boolean) => void;
}

const PatientCallTrends = <T extends string>({
  activeTab,
  className,
  children,
  defaultTab,
  isOpen,
  isLoading = false,
  onTabChange,
  onToggle,
  ...rest
}: PatientCallTrendsProps<T>) => {
  const contextValue = useMemo<TrendsContextValue<T>>(
    () => ({
      activeTab,
      setActiveTab: onTabChange,
      isOpen,
      toggleOpen: () => onToggle(!isOpen),
      isLoading,
    }),
    [activeTab, isOpen, isLoading, onTabChange, onToggle]
  );

  return (
    <TrendsProvider value={contextValue}>
      <div css={[styles.container, className]} {...rest}>
        {children}
      </div>
    </TrendsProvider>
  );
};

const Header = ({ title, duration, isLimitedData, tooltipContent, trackingId }: HeaderProps) => {
  const { isOpen, toggleOpen } = useTrendsContext();
  const { triggerProps, tooltipProps, Tooltip } = useTooltip();

  return (
    <div css={styles.header}>
      <Text size='medium' weight='bold'>
        {title}
      </Text>
      {duration && (
        <NakedButton className='action-wrapper' onClick={toggleOpen} trackingId={trackingId}>
          <Text size='medium' color='subdued'>
            {duration}
          </Text>
          <span {...triggerProps}>
            {isLimitedData && <Icon name='alert-small' color='subdued' />}
            {tooltipContent && <Tooltip {...tooltipProps}>{tooltipContent}</Tooltip>}
          </span>
          <Icon
            className='dropdown-icon'
            name={isOpen ? 'caret-up-small' : 'caret-down-small'}
            height={12}
            width={12}
          />
        </NakedButton>
      )}
    </div>
  );
};

const Content = ({ children }: ContentProps) => {
  const { activeTab, setActiveTab, isOpen } = useTrendsContext();
  if (!isOpen) return null;

  return (
    <Tabs controlledTab={activeTab} onChange={(id: string) => setActiveTab(id)}>
      {children}
    </Tabs>
  );
};

const View = ({ children, ...rest }: ViewProps) => {
  return (
    <Tabs.Panel css={styles.panel} {...rest}>
      {children}
    </Tabs.Panel>
  );
};

PatientCallTrends.Header = Header;
PatientCallTrends.Content = Content;
PatientCallTrends.Navigation = Tabs.Bar;
PatientCallTrends.Item = Tabs.Tab;
PatientCallTrends.View = View;

export default PatientCallTrends;

const styles = {
  container: css``,
  header: css`
    display: flex;
    align-items: center;
    gap: ${theme.spacing(1)};

    .action-wrapper {
      display: flex;
      align-items: center;
      gap: ${theme.spacing(0.5)};

      .dropdown-icon {
        margin-left: ${theme.spacing(0.5)};
      }
    }
  `,
  panel: css`
    margin-top: ${theme.spacing(1)};
  `,
};
